import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService, api } from '../services';
import { useDebug } from './DebugContext';

const AuthContext = createContext();

// Helper function to extract error message from API response
const extractErrorMessage = (error) => {
  if (error.response?.data?.detail) {
    const detail = error.response.data.detail;
    if (Array.isArray(detail)) {
      // Pydantic validation errors
      return detail.map(err => err.msg).join(', ');
    }
    return detail;
  }
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  return error.message || 'An error occurred';
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const { addLog } = useDebug();

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async (retryCount = 0) => {
    const token = authService.getToken();
    if (token) {
      try {
        const response = await api.get('/api/auth/me');
        setUser(response.data);
        addLog('info', 'Auth check successful', { user: response.data.email });
      } catch (error) {
        addLog('error', 'Auth check failed', { error: error.message, retryCount });
        if (retryCount === 0 && error.response?.status === 401) {
          addLog('info', 'Retrying auth check', { retryCount: retryCount + 1 });
          await new Promise(resolve => setTimeout(resolve, 500));
          return checkAuth(retryCount + 1);
        }
        authService.logout();
      }
    }
    setLoading(false);
  };

  const signUp = async (email, password) => {
    try {
      addLog('info', 'Attempting sign up', { email });
      const response = await authService.register(email, password);
      await authService.login(email, password);
      await new Promise(resolve => setTimeout(resolve, 500));
      await checkAuth();
      addLog('success', 'Sign up successful', { email });
      return { data: { user: response }, error: null };
    } catch (error) {
      const errorMessage = extractErrorMessage(error) || 'Registration failed';
      addLog('error', 'Sign up failed', { email, error: errorMessage });
      return { data: null, error: { message: errorMessage } };
    }
  };

  const signIn = async (email, password) => {
    try {
      addLog('info', 'Attempting sign in', { email });
      await authService.login(email, password);
      await new Promise(resolve => setTimeout(resolve, 500));
      await checkAuth();
      addLog('success', 'Sign in successful', { email });
      return { data: { user }, error: null };
    } catch (error) {
      const errorMessage = extractErrorMessage(error) || 'Invalid email or password';
      addLog('error', 'Sign in failed', { email, error: errorMessage });
      return { data: null, error: { message: errorMessage } };
    }
  };

  const signOut = async () => {
    setLoading(true);
    addLog('info', 'Signing out', { email: user?.email });
    authService.logout();
    setUser(null);
    setLoading(false);
    addLog('success', 'Sign out successful');
    return { error: null };
  };

  const updateUserProfile = async (data) => {
    try {
      addLog('info', 'Updating user profile', data);
      const updatedUser = await authService.updateProfile(data);
      setUser(updatedUser);
      addLog('success', 'Profile updated');
      return { data: updatedUser, error: null };
    } catch (error) {
      const errorMessage = extractErrorMessage(error) || 'Update failed';
      addLog('error', 'Profile update failed', { error: errorMessage });
      return { data: null, error: { message: errorMessage } };
    }
  };

  const uploadUserAvatar = async (file) => {
    try {
      addLog('info', 'Uploading avatar');
      const updatedUser = await authService.uploadAvatar(file);
      setUser(updatedUser);
      addLog('success', 'Avatar uploaded');
      return { data: updatedUser, error: null };
    } catch (error) {
      const errorMessage = extractErrorMessage(error) || 'Upload failed';
      addLog('error', 'Avatar upload failed', { error: errorMessage });
      return { data: null, error: { message: errorMessage } };
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      addLog('info', 'Changing password');
      await authService.changePassword(currentPassword, newPassword);
      addLog('success', 'Password changed');
      return { error: null };
    } catch (error) {
      const errorMessage = extractErrorMessage(error) || 'Password change failed';
      addLog('error', 'Password change failed', { error: errorMessage });
      return { data: null, error: { message: errorMessage } };
    }
  };

  const value = {
    user,
    signUp,
    signIn,
    signOut,
    updateUserProfile,
    uploadUserAvatar,
    changePassword,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
